import React, { useState, useEffect, useCallback } from 'react';
import styles from '../styles/Dashboard.module.css';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where, limit, orderBy } from 'firebase/firestore';
import { FaBus, FaRoute, FaMapMarkerAlt, FaUser, FaUsers, FaChevronLeft, FaChevronRight, FaWrench, FaTachometerAlt, FaSignal, FaTwitter, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const libraries = ['places', 'marker'];

// Helper function to check if a trip is currently in session
const isTripInSession = (route, roster = null) => {
  if (!route || !route.time || route.time === 'N/A') {
    return false;
  }

  try {
    // Check roster date range if roster is provided
    if (roster) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Handle Firestore timestamp or ISO string
      let startDate = null;
      if (roster.date) {
        if (roster.date.toDate) {
          startDate = roster.date.toDate();
        } else {
          startDate = new Date(roster.date);
        }
        startDate.setHours(0, 0, 0, 0);
      }

      let endDate = null;
      if (roster.endDate) {
        if (roster.endDate.toDate) {
          endDate = roster.endDate.toDate();
        } else {
          endDate = new Date(roster.endDate);
        }
        endDate.setHours(23, 59, 59, 999);
      }

      if (startDate && today < startDate) {
        return false;
      }

      if (endDate && today > endDate) {
        return false;
      }

      const frequency = roster.frequency || route.rosterFrequency || 'daily';
      const dayOfWeek = today.getDay();

      if (frequency === 'once') {
        if (startDate && today.getTime() !== startDate.getTime()) {
          return false;
        }
      } else if (frequency === 'weekdays') {
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          return false;
        }
        if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
          const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
          if (!roster.weekdays.includes(rosterDayOfWeek)) {
            return false;
          }
        }
      }
    }

    const timeParts = route.time.split(':');
    if (timeParts.length < 2) {
      return false;
    }

    const routeHours = parseInt(timeParts[0], 10);
    const routeMinutes = parseInt(timeParts[1], 10);

    if (isNaN(routeHours) || isNaN(routeMinutes)) {
      return false;
    }

    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    const routeStartTimeInMinutes = routeHours * 60 + routeMinutes;
    const totalDurationSeconds = route.routeMetrics?.totalDurationWithWait || 0;
    if (totalDurationSeconds === 0) {
      return false;
    }

    const routeEndTimeInMinutes = routeStartTimeInMinutes + Math.ceil(totalDurationSeconds / 60);
    const bufferMinutes = 5;
    const adjustedStartTime = routeStartTimeInMinutes - bufferMinutes;

    if (currentTimeInMinutes >= adjustedStartTime && currentTimeInMinutes <= routeEndTimeInMinutes) {
      return true;
    }

    if (routeEndTimeInMinutes > 24 * 60) {
      const nextDayEndTime = routeEndTimeInMinutes - (24 * 60);
      if (currentTimeInMinutes <= nextDayEndTime) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking if trip is in session:', error);
    return false;
  }
};

// Create bus icon for map marker (similar to Track.js)
const createBusIcon = (size = 32) => {
  // Font Awesome 5 bus icon (actual icon path)
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
      <path d="M256 0C390.4 0 480 35.2 480 80V96l0 32c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32l0 160c0 17.7-14.3 32-32 32v32c0 17.7-14.3 32-32 32H384c-17.7 0-32-14.3-32-32V448H160v32c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32l0-32c-17.7 0-32-14.3-32-32l0-160c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h0V96h0V80C32 35.2 121.6 0 256 0zM96 160v96c0 17.7 14.3 32 32 32H240V128H128c-17.7 0-32 14.3-32 32zM272 288H384c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H272V288zM112 400c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zm288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zM352 80c0-8.8-7.2-16-16-16H176c-8.8 0-16 7.2-16 16s7.2 16 16 16H336c8.8 0 16-7.2 16-16z" fill="#1a1a1a" stroke="#000000" stroke-width="1"/>
    </svg>
  `;
  
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
};

const dashboardRadarEvents = [
  { key: 'harsh_braking', label: 'Harsh Braking', color: '#86efac', stroke: '#15803d' },
  { key: 'harsh_acceleration', label: 'Harsh Acceleration', color: '#93c5fd', stroke: '#1d4ed8' },
  { key: 'overspeeding', label: 'Overspeeding', color: '#fca5a5', stroke: '#b91c1c' },
  { key: 'sustained_speeding', label: 'Sustained Speeding', color: '#c4b5fd', stroke: '#6d28d9' }
];

const getEmptyDashboardEventCounts = () => ({
  harsh_braking: 0,
  harsh_acceleration: 0,
  overspeeding: 0,
  sustained_speeding: 0
});

const mapDashboardEventNameToRadarKey = (eventNameRaw) => {
  const normalized = String(eventNameRaw || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!normalized) return null;

  const hasHarsh = normalized.includes('harsh');
  const hasBrake = normalized.includes('brak');
  const hasAccel = normalized.includes('acceler') || normalized.includes('accel');
  const hasSustained = normalized.includes('sustained');
  const hasSpeeding = normalized.includes('speeding') || normalized.includes('overspeed') || normalized.includes('over speed');

  if (hasHarsh && hasBrake) return 'harsh_braking';
  if (hasHarsh && hasAccel) return 'harsh_acceleration';
  if (hasSustained && hasSpeeding) return 'sustained_speeding';
  if (hasSpeeding) return 'overspeeding';

  const exactKey = normalized.replace(/\s+/g, '_');
  if (Object.prototype.hasOwnProperty.call(getEmptyDashboardEventCounts(), exactKey)) {
    return exactKey;
  }
  return null;
};

const createDashboardRadarDataFromCounts = (counts) => dashboardRadarEvents.map((trackedEvent) => ({
  event: trackedEvent.label,
  value: counts[trackedEvent.key] || 0,
  color: trackedEvent.color
}));

const DashboardRadarAngleTick = ({ payload, x, y, cx, cy }) => {
  const words = String(payload?.value || '').split(' ');
  const deltaX = x - cx;
  const deltaY = y - cy;
  const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2) || 1;
  const unitX = deltaX / distance;
  const unitY = deltaY / distance;
  const adjustedX = x + unitX * 18;
  const adjustedY = y + unitY * 14;
  const isLeft = unitX < -0.35;
  const isRight = unitX > 0.35;
  const textAnchor = isLeft ? 'end' : isRight ? 'start' : 'middle';
  const lineHeight = 12;
  const startDy = words.length > 1 ? -((words.length - 1) * lineHeight) / 2 : 0;

  return (
    <text
      x={adjustedX}
      y={adjustedY}
      textAnchor={textAnchor}
      fill="#1f2937"
      fontSize={10}
      fontWeight={600}
    >
      {words.map((word, index) => (
        <tspan key={`${payload?.value}-${word}-${index}`} x={adjustedX} dy={index === 0 ? startDy : lineHeight}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

const Dashboard = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [timeOfDay, setTimeOfDay] = useState('');
  const [activeBuses, setActiveBuses] = useState(0);
  const [scheduledTrips, setScheduledTrips] = useState(0);
  const [studentCount, setStudentCount] = useState(0);
  const [busesInMaintenance, setBusesInMaintenance] = useState(0);
  const [driverCount, setDriverCount] = useState(0);
  const [totalMiles, setTotalMiles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busesWithIgnition, setBusesWithIgnition] = useState([]);
  const [currentBusIndex, setCurrentBusIndex] = useState(0);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [driverProfiles, setDriverProfiles] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [tripsByDate, setTripsByDate] = useState({});
  const [currentTripIndex, setCurrentTripIndex] = useState(0);
  const [fleetAlerts, setFleetAlerts] = useState([]);
  const [tweets, setTweets] = useState([]);
  const [currentTweetIndex, setCurrentTweetIndex] = useState(0);
  const [currentTweetMediaIndex, setCurrentTweetMediaIndex] = useState(0);
  const [loadingTweets, setLoadingTweets] = useState(true);
  
  // Metrics section state
  const [totalFleetDistance, setTotalFleetDistance] = useState(0);
  const [plannedDistance, setPlannedDistance] = useState(0);
  const [previousPlannedDistance, setPreviousPlannedDistance] = useState(0);
  const [actualDistance] = useState(0); // Will be 0 for now
  const [distanceChartData, setDistanceChartData] = useState([]);
  const [tripsChartData, setTripsChartData] = useState([]);
  const [fleetEventCounts, setFleetEventCounts] = useState(() => getEmptyDashboardEventCounts());
  const [fleetEventRadarData, setFleetEventRadarData] = useState(() => createDashboardRadarDataFromCounts(getEmptyDashboardEventCounts()));
  const [chartDuration, setChartDuration] = useState('1d'); // '1d', '1wk', '30days', 'future', 'custom' - shared for both charts
  const [driversOnTrip, setDriversOnTrip] = useState([]);
  const [driversResting, setDriversResting] = useState([]);
  const [showRestingDrivers, setShowRestingDrivers] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const preferredTwitterUsername = (userInfo?.preferredTwitterUsername || 'Ma3Route').replace(/^@/, '').trim() || 'Ma3Route';

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries
  });

  useEffect(() => {
    const fetchUserInfo = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          // First, try to get user document by document ID (matching auth UID)
          let userDoc = await getDoc(doc(db, 'users', user.uid));
          
          // If document doesn't exist with that ID, try querying by uid field
          if (!userDoc.exists()) {
            const usersRef = collection(db, 'users');
            const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
            const querySnapshot = await getDocs(uidQuery);
            
            if (!querySnapshot.empty) {
              // Found user document with uid field matching auth UID
              userDoc = querySnapshot.docs[0];
            }
          }
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserInfo(userData);
            setUserRole(userData.role);
            setCurrentUserId(user.uid);
          } else {
            // Fallback to auth user info
            setUserInfo({
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'User',
              lastName: user.displayName?.split(' ').slice(1).join(' ') || ''
            });
          }
        } catch (err) {
          console.error('Error fetching user info:', err);
          // Fallback to auth user info
          const user = auth.currentUser;
          if (user) {
            setUserInfo({
              firstName: user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'User',
              lastName: user.displayName?.split(' ').slice(1).join(' ') || ''
            });
          }
        }
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const getTimeOfDay = () => {
      const hour = new Date().getHours();
      
      if (hour >= 5 && hour < 12) {
        return 'Morning';
      } else if (hour >= 12 && hour < 17) {
        return 'Afternoon';
      } else {
        return 'Evening';
      }
    };

    setTimeOfDay(getTimeOfDay());
    
    // Update every minute to handle day transitions
    const interval = setInterval(() => {
      setTimeOfDay(getTimeOfDay());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Fetch active buses
        const vehiclesRef = collection(db, 'vehicles');
        const vehiclesSnapshot = await getDocs(vehiclesRef);
        
        // Filter vehicles based on user role
        let filteredVehicles = vehiclesSnapshot.docs;
        if (userRole === 'driver' || userRole === 'assistant') {
          filteredVehicles = vehiclesSnapshot.docs.filter(doc => {
            const data = doc.data();
            return (data.assignedDriver && data.assignedDriver.id === currentUserId) ||
                   (data.assignedAssistant && data.assignedAssistant.id === currentUserId);
          });
        }
        
        const activeBusesCount = filteredVehicles.filter(doc => {
          const data = doc.data();
          return data.status === 'active' || !data.status; // Count active or undefined status as active
        }).length;
        setActiveBuses(activeBusesCount);

        // Fetch student count
        const studentsRef = collection(db, 'students');
        const studentsSnapshot = await getDocs(studentsRef);
        setStudentCount(studentsSnapshot.docs.length);

        // Fetch buses in maintenance
        const maintenanceBusesCount = filteredVehicles.filter(doc => {
          const data = doc.data();
          return data.status === 'maintenance';
        }).length;
        setBusesInMaintenance(maintenanceBusesCount);

        // Fetch driver count
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(query(usersRef, where('role', '==', 'driver')));
        setDriverCount(usersSnapshot.docs.length);

        // Fetch trips scheduled in next 24 hours and calculate total distance
        const routesRef = collection(db, 'optimizedRoutes');
        const routesSnapshot = await getDocs(routesRef);
        const now = new Date();
        const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        
        // Get assigned vehicle IDs for drivers/assistants
        const assignedVehicleIds = new Set();
        if (userRole === 'driver' || userRole === 'assistant') {
          filteredVehicles.forEach(doc => {
            assignedVehicleIds.add(doc.id);
          });
        }
        
        let tripsCount = 0;
        let totalDistanceKm = 0;
        
        // Helper function to parse distance from various formats
        const parseDistance = (distance) => {
          if (!distance) return 0;
          
          // If it's already a number, return it (assuming it's in km)
          if (typeof distance === 'number') {
            return distance;
          }
          
          // If it's a string, try to parse it
          const distanceStr = distance.toString().trim();
          
          // Try to match patterns like "5.2 km", "12.5km", "5.2", etc.
          const kmMatch = distanceStr.match(/([\d.]+)\s*km/i);
          if (kmMatch) {
            return parseFloat(kmMatch[1]);
          }
          
          // Try to match just a number
          const numMatch = distanceStr.match(/([\d.]+)/);
          if (numMatch) {
            return parseFloat(numMatch[1]);
          }
          
          return 0;
        };
        
        // Helper function to parse date consistently
        const parseDate = (dateValue) => {
          if (!dateValue) return null;
          
          if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            return dateValue.toDate();
          }
          if (dateValue instanceof Date) {
            return new Date(dateValue);
          }
          if (typeof dateValue === 'string') {
            const datePart = dateValue.split('T')[0];
            const [year, month, day] = datePart.split('-').map(Number);
            if (year && month && day) {
              return new Date(year, month - 1, day, 0, 0, 0, 0);
            }
            return new Date(dateValue);
          }
          return null;
        };
        
        // Helper function to convert weekday names to numbers (1=Monday, 7=Sunday)
        const convertWeekdayNameToNumber = (weekdayName) => {
          const weekdayMap = {
            'Monday': 1,
            'Tuesday': 2,
            'Wednesday': 3,
            'Thursday': 4,
            'Friday': 5,
            'Saturday': 6,
            'Sunday': 7
          };
          return weekdayMap[weekdayName] || null;
        };
        
        // Process routes with async roster fetching
        const routePromises = routesSnapshot.docs.map(async (routeDoc) => {
          const route = routeDoc.data();
          if (route.status === 'active' && route.time) {
            try {
              // Fetch roster data to get date range and weekdays
              let roster = null;
              if (route.rosterId) {
                try {
                  const rosterDoc = await getDoc(doc(db, 'rosters', route.rosterId));
                  if (rosterDoc.exists()) {
                    roster = rosterDoc.data();
                  }
                } catch (error) {
                  console.error('Error fetching roster:', error);
                }
              }
              
              const frequency = route.rosterFrequency || roster?.frequency || 'daily';
              const rosterWeekdays = roster?.weekdays || [];
              
              // Convert weekday names to numbers if they are strings
              const rosterWeekdaysNumbers = rosterWeekdays.map(day => {
                if (typeof day === 'string') {
                  return convertWeekdayNameToNumber(day);
                }
                return day;
              }).filter(day => day !== null);
              
              // Determine base date from roster or route
              let baseDate = null;
              if (roster && roster.date) {
                baseDate = parseDate(roster.date);
              } else if (route.createdAt) {
                baseDate = parseDate(route.createdAt);
              }
              
              // If no base date, use today
              if (!baseDate || isNaN(baseDate.getTime())) {
                baseDate = new Date();
                baseDate.setHours(0, 0, 0, 0);
              } else {
                baseDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
              }
              
              // Get end date from roster
              let endDate = null;
              if (roster && roster.endDate) {
                endDate = parseDate(roster.endDate);
                if (endDate && !isNaN(endDate.getTime())) {
                  endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);
                } else {
                  endDate = null;
                }
              }
              
              // Parse the time
              const [hours, minutes] = route.time.split(':').map(Number);
              
              // Check today's route time
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const todayRouteTime = new Date();
              todayRouteTime.setHours(hours, minutes, 0, 0);
              
              // Check tomorrow's route time
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              const tomorrowRouteTime = new Date(tomorrow);
              tomorrowRouteTime.setHours(hours, minutes, 0, 0);
              
              // Helper function to check if a date should be included
              const shouldIncludeDate = (checkDate, routeTime) => {
                // Check if date is within roster date range
                if (baseDate && checkDate < baseDate) {
                  return false; // Before roster start date
                }
                if (endDate && checkDate > endDate) {
                  return false; // After roster end date
                }
                
                // Check if route time is within next 24 hours
                if (routeTime < now || routeTime > next24Hours) {
                  return false;
                }
                
                const dayOfWeek = checkDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                
                if (frequency === 'daily') {
                  return true;
                } else if (frequency === 'weekdays') {
                  // Check if it's a weekday (Monday-Friday)
                  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                    // If specific weekdays are set, check if current day is included
                    if (rosterWeekdaysNumbers && rosterWeekdaysNumbers.length > 0) {
                      const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
                      return rosterWeekdaysNumbers.includes(rosterDayOfWeek);
                    } else {
                      return true; // All weekdays if no specific days set
                    }
                  }
                  return false;
                } else if (frequency === 'once') {
                  // For one-time rosters, check if date matches base date exactly
                  const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                  const baseDateOnly = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
                  return checkDateOnly.getTime() === baseDateOnly.getTime();
                }
                return true; // Default to daily
              };
              
              // Check today
              if (shouldIncludeDate(today, todayRouteTime)) {
                tripsCount++;
                const distanceKm = parseDistance(route.routeMetrics?.distance);
                if (distanceKm > 0) {
                  totalDistanceKm += distanceKm;
                }
              }
              
              // Check tomorrow (only if it's within 24 hours)
              if (tomorrowRouteTime <= next24Hours && shouldIncludeDate(tomorrow, tomorrowRouteTime)) {
                tripsCount++;
                const distanceKm = parseDistance(route.routeMetrics?.distance);
                if (distanceKm > 0) {
                  totalDistanceKm += distanceKm;
                }
              }
            } catch (error) {
              console.error('Error processing route time:', error);
            }
          }
        });
        
        // Wait for all routes to be processed
        await Promise.all(routePromises);
        
        setScheduledTrips(tripsCount);
        setTotalMiles(totalDistanceKm);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userRole, currentUserId]);

  // Fetch metrics data
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoadingMetrics(true);
      try {
        // Fetch all devices for odometer data
        const devicesRef = collection(db, 'devices');
        const devicesSnapshot = await getDocs(devicesRef);
        
        // Get assigned vehicle IDs and their device IDs for drivers/assistants
        const assignedDeviceIds = new Set();
        if (userRole === 'driver' || userRole === 'assistant') {
          const vehiclesRef = collection(db, 'vehicles');
          const vehiclesSnapshot = await getDocs(vehiclesRef);
          vehiclesSnapshot.docs.forEach(doc => {
            const vehicle = doc.data();
            if ((vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
                (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId)) {
              if (vehicle.assignedDevice && vehicle.assignedDevice.id) {
                assignedDeviceIds.add(vehicle.assignedDevice.id);
              }
            }
          });
        }
        
        // Deduplicate devices using the same logic as Devices.js
        // Priority: IMEI > deviceName + apiProvider > document ID
        const seenUniqueKeys = new Set();
        const uniqueDevices = [];
        
        devicesSnapshot.docs.forEach(doc => {
          const device = {
            id: doc.id,
            ...doc.data()
          };
          
          // Filter devices for drivers/assistants - only count devices from their assigned buses
          if (userRole === 'driver' || userRole === 'assistant') {
            if (!assignedDeviceIds.has(device.id)) {
              return; // Skip devices not assigned to user's buses
            }
          }
          
          const imei = device.imeiNumber ? String(device.imeiNumber).trim() : null;
          const deviceName = device.deviceName ? String(device.deviceName).trim() : null;
          const apiProvider = device.apiProvider || '';
          
          // Create a unique key: IMEI if available, otherwise deviceName + apiProvider, fallback to document ID
          const uniqueKey = imei 
            ? `imei:${imei}` 
            : (deviceName ? `name:${deviceName}:${apiProvider}` : `id:${device.id}`);
          
          // Only add if we haven't seen this unique key before
          if (!seenUniqueKeys.has(uniqueKey)) {
            seenUniqueKeys.add(uniqueKey);
            uniqueDevices.push(device);
          }
        });
        
        // Calculate total distance from unique devices only
        let totalDistance = 0;
        uniqueDevices.forEach(device => {
          const odometer = parseFloat(device.odometer) || 0;
          totalDistance += odometer;
        });
        
        setTotalFleetDistance(totalDistance);

        // Fetch all routes for trips count
        const routesRef = collection(db, 'optimizedRoutes');
        const routesSnapshot = await getDocs(routesRef);
        const allRoutes = routesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch rosters for date calculations
        const rostersRef = collection(db, 'rosters');
        const rostersSnapshot = await getDocs(rostersRef);
        const rostersMap = {};
        rostersSnapshot.docs.forEach(doc => {
          rostersMap[doc.id] = { id: doc.id, ...doc.data() };
        });

        // Get assigned vehicle IDs for drivers/assistants (used in calculateChartData)
        const assignedVehicleIdsForTrips = new Set();
        if (userRole === 'driver' || userRole === 'assistant') {
          const vehiclesRef = collection(db, 'vehicles');
          const vehiclesSnapshot = await getDocs(vehiclesRef);
          vehiclesSnapshot.docs.forEach(doc => {
            const vehicle = doc.data();
            if ((vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
                (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId)) {
              assignedVehicleIdsForTrips.add(doc.id);
            }
          });
        }

        // Calculate chart data based on duration - separate for distance and trips
        const calculateChartData = (duration, type) => {
          const now = new Date();
          let startDate = new Date();
          let daysToShow = 30;
          let interval = 'day';
          
          switch (duration) {
            case '1d':
              startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
              daysToShow = 24;
              interval = 'hour';
              break;
            case '1wk':
              startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              daysToShow = 7;
              interval = 'day';
              break;
            case '30days':
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              daysToShow = 30;
              interval = 'day';
              break;
            case 'future':
              startDate = now;
              daysToShow = 30; // Show next 30 days
              interval = 'day';
              break;
            case 'custom':
              if (customDateRange.startDate && customDateRange.endDate) {
                startDate = new Date(customDateRange.startDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(customDateRange.endDate);
                endDate.setHours(23, 59, 59, 999);
                const diffTime = Math.abs(endDate - startDate);
                daysToShow = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                interval = daysToShow <= 7 ? 'day' : 'day';
              } else {
                // Default to last 30 days if custom range not set
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                daysToShow = 30;
                interval = 'day';
              }
              break;
            default:
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              daysToShow = 30;
              interval = 'day';
              break;
          }

          const data = [];

          for (let i = 0; i < daysToShow; i++) {
            const date = new Date(startDate);
            if (interval === 'hour') {
              date.setHours(date.getHours() + i);
              date.setMinutes(0, 0, 0);
            } else {
              date.setDate(date.getDate() + i);
              date.setHours(0, 0, 0, 0);
            }

            let value = 0;

            allRoutes.forEach(route => {
              if (route.status === 'active') {
                // Filter routes for drivers/assistants
                if ((userRole === 'driver' || userRole === 'assistant') && route.busId) {
                  if (!assignedVehicleIdsForTrips.has(route.busId)) {
                    return; // Skip routes for buses not assigned to this user
                  }
                }
                
                const roster = route.rosterId ? rostersMap[route.rosterId] : null;
                const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                
                // Helper function to check if route applies to this date
                const routeAppliesToDate = () => {
                  if (roster) {
                    // Parse roster dates - handle Firestore timestamps and ISO strings
                    let rosterStart = null;
                    let rosterEnd = null;
                    
                    if (roster.date) {
                      if (roster.date.toDate && typeof roster.date.toDate === 'function') {
                        rosterStart = roster.date.toDate();
                      } else if (typeof roster.date === 'string') {
                        rosterStart = new Date(roster.date);
                      } else {
                        rosterStart = new Date(roster.date);
                      }
                      // Normalize to start of day for comparison
                      rosterStart = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                    }
                    
                    if (roster.endDate) {
                      if (roster.endDate.toDate && typeof roster.endDate.toDate === 'function') {
                        rosterEnd = roster.endDate.toDate();
                      } else if (typeof roster.endDate === 'string') {
                        rosterEnd = new Date(roster.endDate);
                      } else {
                        rosterEnd = new Date(roster.endDate);
                      }
                      // Normalize to end of day for comparison
                      rosterEnd = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate(), 23, 59, 59, 999);
                    }
                    
                    // Check if date is within roster range (including past dates)
                    // Compare dates by date only (ignore time)
                    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                    if (rosterStart) {
                      const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                      if (checkDateOnly < rosterStartOnly) return false;
                    }
                    if (rosterEnd) {
                      const rosterEndOnly = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate());
                      if (checkDateOnly > rosterEndOnly) return false;
                    }
                    
                    // Check frequency
                    const frequency = roster.frequency || route.rosterFrequency || 'daily';
                    const dayOfWeek = checkDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                    
                    if (frequency === 'once') {
                      // For one-time rosters, only show on the exact start date
                      if (rosterStart) {
                        const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                        const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                        return rosterStartOnly.getTime() === checkDateOnly.getTime();
                      }
                      return false;
                    } else if (frequency === 'weekdays') {
                      // Check if it's a weekday (Monday-Friday)
                      if (dayOfWeek === 0 || dayOfWeek === 6) return false;
                      
                      // Check if specific weekdays are set
                      if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
                        // Convert weekday names to numbers if needed
                        const convertWeekdayNameToNumber = (weekdayName) => {
                          const weekdayMap = {
                            'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
                            'Friday': 5, 'Saturday': 6, 'Sunday': 7
                          };
                          return weekdayMap[weekdayName] || null;
                        };
                        
                        const rosterWeekdaysNumbers = roster.weekdays.map(day => {
                          if (typeof day === 'string') {
                            return convertWeekdayNameToNumber(day);
                          }
                          return day;
                        }).filter(day => day !== null);
                        
                        const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
                        if (!rosterWeekdaysNumbers.includes(rosterDayOfWeek)) return false;
                      }
                    }
                    // For 'daily' frequency, any day within the date range is valid (including past dates)
                    return true;
                  } else {
                    // No roster - treat as recurring route if it has a time
                    // Show on all days within the chart range if route is active
                    if (route.time) {
                      // If route has a time, treat it as a daily recurring route
                      // Check if the route was created before or on the check date
                      const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                      const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                      
                      // Route applies if check date is on or after creation date
                      return checkDate.getTime() >= routeDateOnly.getTime();
                    } else {
                      // No time set - only show on creation date
                      const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                      const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                      return routeDateOnly.getTime() === checkDate.getTime();
                    }
                  }
                };
                
                // Check if route applies to this date
                if (routeAppliesToDate()) {
                  // For hourly view, check if route time matches the hour
                  if (interval === 'hour' && route.time) {
                    const [routeHours] = route.time.split(':').map(Number);
                    const checkHour = date.getHours();
                    if (routeHours === checkHour) {
                      if (type === 'distance') {
                        value += parseFloat(route.routeMetrics?.distance) || 0;
                      } else {
                        value += 1;
                      }
                    }
                  } else if (interval === 'day') {
                    // For daily view, route applies to this date
                    if (type === 'distance') {
                      value += parseFloat(route.routeMetrics?.distance) || 0;
                    } else {
                      value += 1;
                    }
                  }
                }
              }
            });

            const label = interval === 'hour' 
              ? `${date.getHours().toString().padStart(2, '0')}:00`
              : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            data.push({ 
              name: label,
              fullDate: date,
              [type === 'distance' ? 'distance' : 'trips']: value 
            });
          }

          return data;
        };

        setDistanceChartData(calculateChartData(chartDuration, 'distance'));
        setTripsChartData(calculateChartData(chartDuration, 'trips'));

        const getDurationRange = (duration) => {
          const now = new Date();
          let startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          let endDate = now;

          switch (duration) {
            case '1d':
              startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case '1wk':
              startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case '30days':
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case 'future':
              startDate = now;
              endDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
              break;
            case 'custom':
              if (customDateRange.startDate && customDateRange.endDate) {
                startDate = new Date(customDateRange.startDate);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(customDateRange.endDate);
                endDate.setHours(23, 59, 59, 999);
              } else {
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                endDate = now;
              }
              break;
            default:
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
          }

          return { startDate, endDate };
        };

        const { startDate: eventsStartDate, endDate: eventsEndDate } = getDurationRange(chartDuration);
        const eventsRef = collection(db, 'events');
        const eventsQuery = query(
          eventsRef,
          where('lastUpdated', '>=', eventsStartDate.toISOString()),
          orderBy('lastUpdated', 'asc')
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        const aggregatedEventCounts = getEmptyDashboardEventCounts();
        const overspeedStreakByVehicle = new Map();
        const minCoordinateDelta = 0.00001;
        const maxOverspeedGapMs = 5 * 60 * 1000;

        const hasMeaningfulMovement = (previousPoint, nextPoint) => {
          if (!previousPoint || !nextPoint) return false;
          if (!previousPoint.hasValidCoordinate || !nextPoint.hasValidCoordinate) return false;
          return (
            Math.abs(previousPoint.position.lat - nextPoint.position.lat) >= minCoordinateDelta ||
            Math.abs(previousPoint.position.lng - nextPoint.position.lng) >= minCoordinateDelta
          );
        };

        const flushOverspeedStreak = (vehicleId) => {
          const streak = overspeedStreakByVehicle.get(vehicleId) || [];
          if (streak.length === 0) return;

          const movedAcrossCoordinates = streak.some((point, index, arr) => (
            index > 0 && hasMeaningfulMovement(arr[index - 1], point)
          ));

          if (streak.length >= 2 && movedAcrossCoordinates) {
            aggregatedEventCounts.sustained_speeding += 1;
          } else {
            aggregatedEventCounts.overspeeding += streak.length;
          }

          overspeedStreakByVehicle.set(vehicleId, []);
        };

        eventsSnapshot.docs.forEach((eventDoc) => {
          const eventData = eventDoc.data();
          const vehicleId = eventData.vehicleId || '__unknown_vehicle__';
          const eventDateMs = new Date(eventData.lastUpdated).getTime();
          const eventName = eventData.eventName;

          if (!eventDateMs || Number.isNaN(eventDateMs)) return;
          if (eventDateMs > eventsEndDate.getTime()) return;

          if ((userRole === 'driver' || userRole === 'assistant') && vehicleId && !assignedVehicleIdsForTrips.has(vehicleId)) {
            return;
          }

          const mappedEventKey = mapDashboardEventNameToRadarKey(eventName);
          if (mappedEventKey === 'overspeeding') {
            const lat = parseFloat(eventData.lat);
            const lon = parseFloat(eventData.lon);
            const hasValidCoordinate = Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
            const existingStreak = overspeedStreakByVehicle.get(vehicleId) || [];
            if (existingStreak.length > 0) {
              const previousPoint = existingStreak[existingStreak.length - 1];
              const gapMs = eventDateMs - (previousPoint.timestampMs || 0);
              if (gapMs > maxOverspeedGapMs) {
                flushOverspeedStreak(vehicleId);
              }
            }
            const refreshedStreak = overspeedStreakByVehicle.get(vehicleId) || [];
            refreshedStreak.push({
              timestampMs: eventDateMs,
              hasValidCoordinate,
              position: {
                lat: Number.isNaN(lat) ? 0 : lat,
                lng: Number.isNaN(lon) ? 0 : lon
              }
            });
            overspeedStreakByVehicle.set(vehicleId, refreshedStreak);
            return;
          }

          if (mappedEventKey && Object.prototype.hasOwnProperty.call(aggregatedEventCounts, mappedEventKey)) {
            aggregatedEventCounts[mappedEventKey] += 1;
          }
        });

        overspeedStreakByVehicle.forEach((_, vehicleId) => {
          flushOverspeedStreak(vehicleId);
        });

        setFleetEventCounts(aggregatedEventCounts);
        setFleetEventRadarData(createDashboardRadarDataFromCounts(aggregatedEventCounts));

        // Calculate planned distance based on chartDuration
        const calculatePlannedDistance = (duration) => {
          // Helper function to parse distance from various formats
          const parseDistance = (distance) => {
            if (!distance) return 0;
            
            // If it's already a number, return it (assuming it's in km)
            if (typeof distance === 'number') {
              return distance;
            }
            
            // If it's a string, try to parse it
            const distanceStr = distance.toString().trim();
            
            // Try to match patterns like "5.2 km", "12.5km", "5.2", etc.
            const kmMatch = distanceStr.match(/([\d.]+)\s*km/i);
            if (kmMatch) {
              return parseFloat(kmMatch[1]);
            }
            
            // Try to match just a number
            const numMatch = distanceStr.match(/([\d.]+)/);
            if (numMatch) {
              return parseFloat(numMatch[1]);
            }
            
            return 0;
          };

          const now = new Date();
          let startDate = new Date();
          let endDate = new Date();
          
          switch (duration) {
            case '1d':
              startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case '1wk':
              startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case '30days':
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
            case 'future':
              startDate = now;
              endDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
              break;
            case 'custom':
              if (customDateRange.startDate && customDateRange.endDate) {
                startDate = new Date(customDateRange.startDate);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(customDateRange.endDate);
                endDate.setHours(23, 59, 59, 999);
              } else {
                // Default to last 30 days if custom range not set
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                endDate = now;
              }
              break;
            default:
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              endDate = now;
              break;
          }

          let totalPlannedDistance = 0;

          allRoutes.forEach(route => {
            if (route.status === 'active') {
              // Filter routes for drivers/assistants
              if ((userRole === 'driver' || userRole === 'assistant') && route.busId) {
                if (!assignedVehicleIdsForTrips.has(route.busId)) {
                  return; // Skip routes for buses not assigned to this user
                }
              }
              
              const roster = route.rosterId ? rostersMap[route.rosterId] : null;
              
              // Helper function to check if route applies to date range
              const routeAppliesToDateRange = (checkDate) => {
                if (roster) {
                  let rosterStart = null;
                  let rosterEnd = null;
                  
                  if (roster.date) {
                    if (roster.date.toDate && typeof roster.date.toDate === 'function') {
                      rosterStart = roster.date.toDate();
                    } else if (typeof roster.date === 'string') {
                      rosterStart = new Date(roster.date);
                    } else {
                      rosterStart = new Date(roster.date);
                    }
                    rosterStart = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                  }
                  
                  if (roster.endDate) {
                    if (roster.endDate.toDate && typeof roster.endDate.toDate === 'function') {
                      rosterEnd = roster.endDate.toDate();
                    } else if (typeof roster.endDate === 'string') {
                      rosterEnd = new Date(roster.endDate);
                    } else {
                      rosterEnd = new Date(roster.endDate);
                    }
                    rosterEnd = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate(), 23, 59, 59, 999);
                  }
                  
                  const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                  if (rosterStart) {
                    const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                    if (checkDateOnly < rosterStartOnly) return false;
                  }
                  if (rosterEnd) {
                    const rosterEndOnly = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate());
                    if (checkDateOnly > rosterEndOnly) return false;
                  }
                  
                  const frequency = roster.frequency || route.rosterFrequency || 'daily';
                  const dayOfWeek = checkDate.getDay();
                  
                  if (frequency === 'once') {
                    if (rosterStart) {
                      const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                      const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                      return rosterStartOnly.getTime() === checkDateOnly.getTime();
                    }
                    return false;
                  } else if (frequency === 'weekdays') {
                    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
                    
                    if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
                      const convertWeekdayNameToNumber = (weekdayName) => {
                        const weekdayMap = {
                          'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
                          'Friday': 5, 'Saturday': 6, 'Sunday': 7
                        };
                        return weekdayMap[weekdayName] || null;
                      };
                      
                      const rosterWeekdaysNumbers = roster.weekdays.map(day => {
                        if (typeof day === 'string') {
                          return convertWeekdayNameToNumber(day);
                        }
                        return day;
                      }).filter(day => day !== null);
                      
                      const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
                      if (!rosterWeekdaysNumbers.includes(rosterDayOfWeek)) return false;
                    }
                  }
                  return true;
                } else {
                  if (route.time) {
                    const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                    const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                    return checkDateOnly.getTime() >= routeDateOnly.getTime();
                  } else {
                    const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                    const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                    return routeDateOnly.getTime() === checkDateOnly.getTime();
                  }
                }
              };

              // Iterate through each day in the date range
              const currentDate = new Date(startDate);
              while (currentDate <= endDate) {
                const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                
                if (routeAppliesToDateRange(checkDate)) {
                  const distanceKm = parseDistance(route.routeMetrics?.distance);
                  totalPlannedDistance += distanceKm;
                }
                
                currentDate.setDate(currentDate.getDate() + 1);
              }
            }
          });

          return totalPlannedDistance;
        };

        const currentPlannedDistance = calculatePlannedDistance(chartDuration);
        setPlannedDistance(currentPlannedDistance);

        // Calculate previous duration's planned distance for comparison
        const getPreviousDurationPeriod = (currentDuration) => {
          const now = new Date();
          let startDate = new Date();
          let endDate = new Date();
          
          switch (currentDuration) {
            case '1d':
              // Previous day (24 hours before the current period)
              endDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
              startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
              break;
            case '1wk':
              // Previous week (7 days before the current period)
              endDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
              break;
            case '30days':
              // Previous 30 days (30 days before the current period)
              endDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
              break;
            case 'future':
              // For future, compare with last 30 days
              endDate = now;
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              break;
            case 'custom':
              if (customDateRange.startDate && customDateRange.endDate) {
                const customStart = new Date(customDateRange.startDate);
                const customEnd = new Date(customDateRange.endDate);
                const diffTime = Math.abs(customEnd - customStart);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                endDate = new Date(customStart.getTime() - 24 * 60 * 60 * 1000);
                endDate.setHours(23, 59, 59, 999);
                startDate = new Date(endDate.getTime() - diffDays * 24 * 60 * 60 * 1000);
                startDate.setHours(0, 0, 0, 0);
              } else {
                return { startDate: null, endDate: null };
              }
              break;
            default:
              return { startDate: null, endDate: null };
          }
          
          return { startDate, endDate };
        };

        const previousPeriod = getPreviousDurationPeriod(chartDuration);
        if (previousPeriod.startDate && previousPeriod.endDate) {
          // Calculate planned distance for previous period using the same logic
          let previousTotalPlannedDistance = 0;
          
          allRoutes.forEach(route => {
            if (route.status === 'active') {
              // Filter routes for drivers/assistants
              if ((userRole === 'driver' || userRole === 'assistant') && route.busId) {
                if (!assignedVehicleIdsForTrips.has(route.busId)) {
                  return; // Skip routes for buses not assigned to this user
                }
              }
              
              const roster = route.rosterId ? rostersMap[route.rosterId] : null;
              
              const parseDistance = (distance) => {
                if (!distance) return 0;
                if (typeof distance === 'number') return distance;
                const distanceStr = distance.toString().trim();
                const kmMatch = distanceStr.match(/([\d.]+)\s*km/i);
                if (kmMatch) return parseFloat(kmMatch[1]);
                const numMatch = distanceStr.match(/([\d.]+)/);
                if (numMatch) return parseFloat(numMatch[1]);
                return 0;
              };

              const routeAppliesToDateRange = (checkDate) => {
                if (roster) {
                  let rosterStart = null;
                  let rosterEnd = null;
                  
                  if (roster.date) {
                    if (roster.date.toDate && typeof roster.date.toDate === 'function') {
                      rosterStart = roster.date.toDate();
                    } else if (typeof roster.date === 'string') {
                      rosterStart = new Date(roster.date);
                    } else {
                      rosterStart = new Date(roster.date);
                    }
                    rosterStart = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                  }
                  
                  if (roster.endDate) {
                    if (roster.endDate.toDate && typeof roster.endDate.toDate === 'function') {
                      rosterEnd = roster.endDate.toDate();
                    } else if (typeof roster.endDate === 'string') {
                      rosterEnd = new Date(roster.endDate);
                    } else {
                      rosterEnd = new Date(roster.endDate);
                    }
                    rosterEnd = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate(), 23, 59, 59, 999);
                  }
                  
                  const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                  if (rosterStart) {
                    const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                    if (checkDateOnly < rosterStartOnly) return false;
                  }
                  if (rosterEnd) {
                    const rosterEndOnly = new Date(rosterEnd.getFullYear(), rosterEnd.getMonth(), rosterEnd.getDate());
                    if (checkDateOnly > rosterEndOnly) return false;
                  }
                  
                  const frequency = roster.frequency || route.rosterFrequency || 'daily';
                  const dayOfWeek = checkDate.getDay();
                  
                  if (frequency === 'once') {
                    if (rosterStart) {
                      const rosterStartOnly = new Date(rosterStart.getFullYear(), rosterStart.getMonth(), rosterStart.getDate());
                      const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                      return rosterStartOnly.getTime() === checkDateOnly.getTime();
                    }
                    return false;
                  } else if (frequency === 'weekdays') {
                    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
                    
                    if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
                      const convertWeekdayNameToNumber = (weekdayName) => {
                        const weekdayMap = {
                          'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
                          'Friday': 5, 'Saturday': 6, 'Sunday': 7
                        };
                        return weekdayMap[weekdayName] || null;
                      };
                      
                      const rosterWeekdaysNumbers = roster.weekdays.map(day => {
                        if (typeof day === 'string') {
                          return convertWeekdayNameToNumber(day);
                        }
                        return day;
                      }).filter(day => day !== null);
                      
                      const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
                      if (!rosterWeekdaysNumbers.includes(rosterDayOfWeek)) return false;
                    }
                  }
                  return true;
                } else {
                  if (route.time) {
                    const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                    const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                    return checkDateOnly.getTime() >= routeDateOnly.getTime();
                  } else {
                    const routeDate = route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0);
                    const routeDateOnly = new Date(routeDate.getFullYear(), routeDate.getMonth(), routeDate.getDate());
                    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
                    return routeDateOnly.getTime() === checkDateOnly.getTime();
                  }
                }
              };

              const currentDate = new Date(previousPeriod.startDate);
              while (currentDate <= previousPeriod.endDate) {
                const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                
                if (routeAppliesToDateRange(checkDate)) {
                  const distanceKm = parseDistance(route.routeMetrics?.distance);
                  previousTotalPlannedDistance += distanceKm;
                }
                
                currentDate.setDate(currentDate.getDate() + 1);
              }
            }
          });
          
          setPreviousPlannedDistance(previousTotalPlannedDistance);
        } else {
          setPreviousPlannedDistance(0);
        }

        // Fetch drivers and their trip status
        const vehiclesRef = collection(db, 'vehicles');
        const vehiclesSnapshot = await getDocs(vehiclesRef);
        
        // Filter vehicles for drivers/assistants
        let filteredVehiclesForDrivers = vehiclesSnapshot.docs;
        if (userRole === 'driver' || userRole === 'assistant') {
          filteredVehiclesForDrivers = vehiclesSnapshot.docs.filter(doc => {
            const data = doc.data();
            return (data.assignedDriver && data.assignedDriver.id === currentUserId) ||
                   (data.assignedAssistant && data.assignedAssistant.id === currentUserId);
          });
        }
        
        const usersRef = collection(db, 'users');
        const driversSnapshot = await getDocs(query(usersRef, where('role', '==', 'driver')));
        
        const driversOnTripList = [];
        const driversRestingList = [];

        driversSnapshot.docs.forEach(driverDoc => {
          const driver = { id: driverDoc.id, ...driverDoc.data() };
          
          // For drivers/assistants, only show themselves in the drivers status
          if (userRole === 'driver' || userRole === 'assistant') {
            if (driver.id !== currentUserId) {
              return; // Skip other drivers
            }
          }
          
          const assignedVehicle = filteredVehiclesForDrivers.find(vDoc => {
            const vehicle = vDoc.data();
            return vehicle.assignedDriver && vehicle.assignedDriver.id === driver.id;
          });

          if (assignedVehicle) {
            const vehicle = assignedVehicle.data();
            const vehicleRoutes = allRoutes.filter(r => r.busId === assignedVehicle.id);
            let isOnTrip = false;
            let currentRoute = null;

            for (const route of vehicleRoutes) {
              const roster = rostersMap[route.rosterId] || null;
              if (isTripInSession(route, roster)) {
                isOnTrip = true;
                currentRoute = route;
                break;
              }
            }

            const driverInfo = {
              ...driver,
              vehicle: {
                id: assignedVehicle.id,
                registrationNumber: vehicle.registrationNumber || vehicle.fleetNumber || 'N/A',
                ...vehicle
              },
              route: currentRoute
            };

            if (isOnTrip) {
              driversOnTripList.push(driverInfo);
            } else {
              driversRestingList.push(driverInfo);
            }
          } else {
            // Driver not assigned to any vehicle
            driversRestingList.push({
              ...driver,
              vehicle: null,
              route: null
            });
          }
        });

        setDriversOnTrip(driversOnTripList);
        setDriversResting(driversRestingList);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchMetrics();
    // Refresh metrics every 5 minutes
    const interval = setInterval(fetchMetrics, 300000);
    return () => clearInterval(interval);
  }, [chartDuration, customDateRange, userRole, currentUserId]);

  // Fetch fleet alerts
  useEffect(() => {
    const fetchFleetAlerts = async () => {
      try {
        const alerts = [];
        const now = new Date();
        const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000); // Increased to 30 minutes

        // Fetch vehicles, devices, and routes
        const vehiclesRef = collection(db, 'vehicles');
        const vehiclesSnapshot = await getDocs(vehiclesRef);
        
        // Filter vehicles for drivers/assistants
        let filteredVehicles = vehiclesSnapshot.docs;
        if (userRole === 'driver' || userRole === 'assistant') {
          filteredVehicles = vehiclesSnapshot.docs.filter(doc => {
            const data = doc.data();
            return (data.assignedDriver && data.assignedDriver.id === currentUserId) ||
                   (data.assignedAssistant && data.assignedAssistant.id === currentUserId);
          });
        }
        
        // Get assigned vehicle IDs for filtering routes
        const assignedVehicleIds = new Set();
        if (userRole === 'driver' || userRole === 'assistant') {
          filteredVehicles.forEach(doc => {
            assignedVehicleIds.add(doc.id);
          });
        }
        
        const devicesRef = collection(db, 'devices');
        const devicesSnapshot = await getDocs(devicesRef);
        const routesRef = collection(db, 'optimizedRoutes');
        const routesSnapshot = await getDocs(routesRef);
        
        const devicesMap = {};
        devicesSnapshot.docs.forEach(doc => {
          devicesMap[doc.id] = { id: doc.id, ...doc.data() };
        });

        // Create a map of routes by busId
        const routesByBusId = {};
        routesSnapshot.docs.forEach(routeDoc => {
          const route = routeDoc.data();
          
          // Filter routes for drivers/assistants - only include routes for their assigned buses
          if ((userRole === 'driver' || userRole === 'assistant') && route.busId) {
            if (!assignedVehicleIds.has(route.busId)) {
              return; // Skip routes for buses not assigned to this user
            }
          }
          
          if (route.busId) {
            if (!routesByBusId[route.busId]) {
              routesByBusId[route.busId] = [];
            }
            routesByBusId[route.busId].push({
              id: routeDoc.id,
              ...route,
              createdAt: route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0),
              updatedAt: route.updatedAt?.toDate ? route.updatedAt.toDate() : (route.createdAt?.toDate ? route.createdAt.toDate() : new Date(route.createdAt || 0))
            });
          }
        });

        // Check for buses with ignition on (recently turned on)
        filteredVehicles.forEach(vehicleDoc => {
          const vehicle = vehicleDoc.data();
          const vehicleId = vehicleDoc.id;
          
          if (vehicle.assignedDevice && vehicle.assignedDevice.id) {
            const device = devicesMap[vehicle.assignedDevice.id];
            if (device && device.isIgnitionOn) {
              // Check if ignition was recently turned on (within last 30 minutes)
              const lastUpdate = device.lastUpdate?.toDate ? device.lastUpdate.toDate() : new Date(device.lastUpdate || 0);
              if (lastUpdate >= thirtyMinutesAgo) {
                alerts.push({
                  id: `ignition-${vehicleId}-${lastUpdate.getTime()}`,
                  type: 'info',
                  icon: FaBus,
                  title: 'Bus Ignition On',
                  message: `${vehicle.registrationNumber || vehicle.fleetNumber || 'Bus'} has turned on ignition`,
                  timestamp: lastUpdate,
                  vehicleId: vehicleId
                });
              }
            }

            // Check for weak signal
            if (device && device.signalStrength !== undefined) {
              const signalStrength = parseFloat(device.signalStrength) || 0;
              if (signalStrength < 20) { // Weak signal threshold
                alerts.push({
                  id: `signal-${vehicleId}`,
                  type: 'warning',
                  icon: FaSignal,
                  title: 'Weak Device Signal',
                  message: `${vehicle.registrationNumber || vehicle.fleetNumber || 'Bus'} has weak signal (${signalStrength}%)`,
                  timestamp: device.lastUpdate?.toDate ? device.lastUpdate.toDate() : new Date(),
                  vehicleId: vehicleId
                });
              }
            }
          }

          // Check for recent trip assignments (routes with busId matching this vehicle)
          // Only show routes for assigned buses for drivers/assistants
          if (routesByBusId[vehicleId]) {
            routesByBusId[vehicleId].forEach(route => {
              // Check if route was created or updated recently
              const routeTimestamp = route.updatedAt > route.createdAt ? route.updatedAt : route.createdAt;
              if (routeTimestamp >= thirtyMinutesAgo) {
                alerts.push({
                  id: `trip-${route.id}-${routeTimestamp.getTime()}`,
                  type: 'info',
                  icon: FaRoute,
                  title: 'Trip Assigned',
                  message: `Trip "${route.name || 'Unnamed Trip'}" assigned to ${vehicle.registrationNumber || vehicle.fleetNumber || 'Bus'}`,
                  timestamp: routeTimestamp,
                  vehicleId: vehicleId
                });
              }
            });
          }

          // Check for recent driver assignments
          if (vehicle.assignedDriver && vehicle.lastAssignmentUpdate) {
            const driverAssignedAt = new Date(vehicle.lastAssignmentUpdate);
            if (driverAssignedAt >= thirtyMinutesAgo && !isNaN(driverAssignedAt.getTime())) {
              alerts.push({
                id: `driver-${vehicleId}-${driverAssignedAt.getTime()}`,
                type: 'info',
                icon: FaUser,
                title: 'Driver Assigned',
                message: `${vehicle.assignedDriver.name || 'Driver'} assigned to ${vehicle.registrationNumber || vehicle.fleetNumber || 'Bus'}`,
                timestamp: driverAssignedAt,
                vehicleId: vehicleId
              });
            }
          }
        });

        // Remove duplicates based on id
        const uniqueAlerts = alerts.reduce((acc, alert) => {
          if (!acc.find(a => a.id === alert.id)) {
            acc.push(alert);
          }
          return acc;
        }, []);

        // Sort alerts by timestamp (newest first)
        uniqueAlerts.sort((a, b) => b.timestamp - a.timestamp);
        
        // Limit to most recent 10 alerts
        setFleetAlerts(uniqueAlerts.slice(0, 10));
      } catch (error) {
        console.error('Error fetching fleet alerts:', error);
      }
    };

    fetchFleetAlerts();
    // Refresh alerts every 10 seconds for more real-time updates
    const interval = setInterval(fetchFleetAlerts, 10000);
    return () => clearInterval(interval);
  }, [userRole, currentUserId]);

  // Fetch tweets from preferred Twitter account via proxy
  useEffect(() => {
    const fetchTweets = async () => {
      setLoadingTweets(true);
      try {
        // Use the backend proxy endpoint to avoid CORS issues
        const response = await fetch(`/api/twitter?username=${encodeURIComponent(preferredTwitterUsername)}`);
        
      

        // Parse response data - handle both success and error responses
        let data;
        try {
          data = await response.json();
          
        } catch (parseError) {
          console.error('[Twitter Debug] Failed to parse response:', parseError);
          throw new Error('Invalid response from server');
        }
        
        // Handle rate limiting - if we get cached data, use it silently
        if (response.status === 429 || data.rateLimit) {
          // If we have cached data from rate limit response, use it
          if (data.tweets && data.tweets.length > 0) {
            const formattedTweets = data.tweets.map(tweet => ({
              id: tweet.id,
              text: tweet.text,
              createdAt: new Date(tweet.createdAt),
              author: tweet.author || preferredTwitterUsername,
              mediaUrls: Array.isArray(tweet.mediaUrls)
                ? tweet.mediaUrls
                : (tweet.image ? [tweet.image] : [])
            }));
            setTweets(formattedTweets);
            if (formattedTweets.length > 0) {
              setCurrentTweetIndex(0);
            }
            return;
          }
          // No cached data available, silently return
          return;
        }

        if (!response.ok) {
          const errorData = data || {};
        
          throw new Error(errorData.error || `Failed to fetch tweets: ${response.status}`);
        }
        
        // Success response
        if (data.tweets && Array.isArray(data.tweets) && data.tweets.length > 0) {
          const formattedTweets = data.tweets.map(tweet => ({
            id: tweet.id,
            text: tweet.text,
            createdAt: new Date(tweet.createdAt),
            author: tweet.author || preferredTwitterUsername,
            mediaUrls: Array.isArray(tweet.mediaUrls)
              ? tweet.mediaUrls
              : (tweet.image ? [tweet.image] : [])
          }));
          
          setTweets(formattedTweets);
          if (formattedTweets.length > 0) {
            setCurrentTweetIndex(0);
          }
        } else {
          setTweets([]);
        }
      } catch (error) {
        console.error('[Twitter Debug] Error fetching tweets:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        // Don't clear existing tweets on error, keep what we have
        setTweets(prevTweets => prevTweets.length > 0 ? prevTweets : []);
      } finally {
        setLoadingTweets(false);
      }
    };

    // Fetch only once on mount - rely on server-side cache (4 hours) to minimize API calls
    fetchTweets();
    // No automatic refresh - tweets are cached server-side for 4 hours
    // This minimizes Twitter API calls and prevents rate limit issues
  }, [preferredTwitterUsername]);

  // Get trips for calendar (current month)
  useEffect(() => {
    const fetchCalendarTrips = async () => {
      try {
        const routesRef = collection(db, 'optimizedRoutes');
        const routesSnapshot = await getDocs(routesRef);
        
        // Get assigned vehicle IDs for drivers/assistants
        const assignedVehicleIds = new Set();
        if (userRole === 'driver' || userRole === 'assistant') {
          const vehiclesRef = collection(db, 'vehicles');
          const vehiclesSnapshot = await getDocs(vehiclesRef);
          vehiclesSnapshot.docs.forEach(doc => {
            const vehicle = doc.data();
            if ((vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
                (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId)) {
              assignedVehicleIds.add(doc.id);
            }
          });
        }
        
        const tripsByDateMap = {};
        const currentMonth = calendarMonth.getMonth();
        const currentYear = calendarMonth.getFullYear();
        
        // Get first and last day of month
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        
        // Process routes with async roster fetching
        const routePromises = routesSnapshot.docs.map(async (routeDoc) => {
          const route = routeDoc.data();
          
          // Filter routes for drivers/assistants - only show routes for their assigned buses
          if ((userRole === 'driver' || userRole === 'assistant') && route.busId) {
            if (!assignedVehicleIds.has(route.busId)) {
              return; // Skip routes for buses not assigned to this user
            }
          }
          
          // Check if route has required fields
          if (!route.status || route.status !== 'active') {
            return; // Skip inactive routes
          }
          if (!route.time) {
            return; // Skip routes without time
          }
          
          try {
            // Fetch roster data to get date range
            let roster = null;
            if (route.rosterId) {
              try {
                const rosterDoc = await getDoc(doc(db, 'rosters', route.rosterId));
                if (rosterDoc.exists()) {
                  roster = rosterDoc.data();
                }
              } catch (error) {
                console.error('Error fetching roster:', error);
              }
            }
            
            const [hours, minutes] = route.time.split(':').map(Number);
            const frequency = route.rosterFrequency || roster?.frequency || 'daily';
            const rosterWeekdays = roster?.weekdays || [];
            
            // Helper function to convert weekday names to numbers (1=Monday, 7=Sunday)
            const convertWeekdayNameToNumber = (weekdayName) => {
              const weekdayMap = {
                'Monday': 1,
                'Tuesday': 2,
                'Wednesday': 3,
                'Thursday': 4,
                'Friday': 5,
                'Saturday': 6,
                'Sunday': 7
              };
              return weekdayMap[weekdayName] || null;
            };
            
            // Convert weekday names to numbers if they are strings
            const rosterWeekdaysNumbers = rosterWeekdays.map(day => {
              if (typeof day === 'string') {
                return convertWeekdayNameToNumber(day);
              }
              return day; // Already a number
            }).filter(day => day !== null);
            
            // Helper function to parse date consistently and normalize to local date
            const parseDate = (dateValue) => {
              if (!dateValue) return null;
              
              let parsedDate = null;
              
              // Firestore Timestamp
              if (dateValue.toDate && typeof dateValue.toDate === 'function') {
                parsedDate = dateValue.toDate();
              }
              // Already a Date object
              else if (dateValue instanceof Date) {
                parsedDate = new Date(dateValue);
              }
              // String - handle ISO format
              else if (typeof dateValue === 'string') {
                // Extract just the date part (YYYY-MM-DD)
                const datePart = dateValue.split('T')[0];
                // Create date in local timezone to avoid timezone issues
                const [year, month, day] = datePart.split('-').map(Number);
                if (year && month && day) {
                  // Create date at midnight in local timezone
                  parsedDate = new Date(year, month - 1, day, 0, 0, 0, 0);
                } else {
                  // Fallback to direct parsing
                  parsedDate = new Date(dateValue);
                }
              }
              
              if (!parsedDate || isNaN(parsedDate.getTime())) {
                return null;
              }
              
              // Normalize to local date (remove time component)
              return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
            };
            
            // Determine base date from roster or route
            let baseDate = null;
            if (roster && roster.date) {
              baseDate = parseDate(roster.date);
            } else if (route.createdAt) {
              baseDate = parseDate(route.createdAt);
            }
            
            // If no base date from roster or createdAt, use start of current month
            if (!baseDate || isNaN(baseDate.getTime())) {
              baseDate = new Date(currentYear, currentMonth, 1);
            }
            
            // For routes without roster, ensure baseDate is not in the past relative to current month
            // If baseDate is before current month, use start of current month
            if (!roster && baseDate) {
              const currentMonthStart = new Date(currentYear, currentMonth, 1);
              if (baseDate < currentMonthStart) {
                baseDate = new Date(currentMonthStart);
              }
            }
            
            // Get end date from roster
            let endDate = null;
            if (roster && roster.endDate) {
              endDate = parseDate(roster.endDate);
            }
            
            // Helper function to compare dates by date only (ignore time)
            const compareDates = (date1, date2) => {
              const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
              const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
              return d1.getTime() - d2.getTime();
            };
            
            // Generate trips for each day in the month based on frequency and date range
            for (let day = 1; day <= lastDay.getDate(); day++) {
              const checkDate = new Date(currentYear, currentMonth, day);
              
              // Check if date is within roster date range
              // Must be >= baseDate (roster start date) and <= endDate (roster end date, if set)
              if (compareDates(checkDate, baseDate) < 0) {
                continue; // Before roster start date
              }
              
              if (endDate && compareDates(checkDate, endDate) > 0) {
                continue; // After roster end date
              }
              
              const dayOfWeek = checkDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
              let shouldInclude = false;
              
              if (frequency === 'daily') {
                shouldInclude = true;
              } else if (frequency === 'weekdays') {
                // Check if it's a weekday (Monday-Friday)
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                  // If specific weekdays are set, check if current day is included
                  if (rosterWeekdaysNumbers && rosterWeekdaysNumbers.length > 0) {
                    const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to 1-7 format (1=Monday, 7=Sunday)
                    shouldInclude = rosterWeekdaysNumbers.includes(rosterDayOfWeek);
                  } else {
                    shouldInclude = true; // All weekdays if no specific days set
                  }
                }
              } else if (frequency === 'once') {
                // For one-time rosters, check if date matches base date exactly
                shouldInclude = compareDates(checkDate, baseDate) === 0;
              } else {
                shouldInclude = true; // Default to daily
              }
              
              if (shouldInclude) {
                const tripDate = new Date(checkDate);
                tripDate.setHours(hours, minutes, 0, 0);
                
                // Format date key using local timezone (same format as component-level formatDateKey)
                const year = checkDate.getFullYear();
                const month = String(checkDate.getMonth() + 1).padStart(2, '0');
                const dayStr = String(checkDate.getDate()).padStart(2, '0');
                const dateKey = `${year}-${month}-${dayStr}`;
                
                if (!tripsByDateMap[dateKey]) {
                  tripsByDateMap[dateKey] = [];
                }
                
                tripsByDateMap[dateKey].push({
                  id: routeDoc.id,
                  date: tripDate,
                  name: route.name || 'Trip',
                  time: route.time,
                  tripType: route.tripType || 'AM',
                  route: route
                });
              }
            }
          } catch (error) {
            console.error('Error processing trip time:', error);
          }
        });
        
        // Wait for all routes to be processed
        await Promise.all(routePromises);
        
        // Sort trips within each date by time
        Object.keys(tripsByDateMap).forEach(dateKey => {
          tripsByDateMap[dateKey].sort((a, b) => {
            const timeA = a.time.split(':').map(Number);
            const timeB = b.time.split(':').map(Number);
            return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
          });
        });
        
        setTripsByDate(tripsByDateMap);
      } catch (error) {
        console.error('Error fetching calendar trips:', error);
      }
    };
    
    fetchCalendarTrips();
  }, [calendarMonth, userRole, currentUserId]);

  const fetchBusesWithIgnition = useCallback(async () => {
    setLoadingBuses(true);
    try {
      // Fetch all vehicles
      const vehiclesRef = collection(db, 'vehicles');
      const vehiclesSnapshot = await getDocs(vehiclesRef);
      
      // Filter vehicles for drivers/assistants
      let filteredVehicles = vehiclesSnapshot.docs;
      if (userRole === 'driver' || userRole === 'assistant') {
        filteredVehicles = vehiclesSnapshot.docs.filter(doc => {
          const data = doc.data();
          return (data.assignedDriver && data.assignedDriver.id === currentUserId) ||
                 (data.assignedAssistant && data.assignedAssistant.id === currentUserId);
        });
      }
      
      // Fetch all devices
      const devicesRef = collection(db, 'devices');
      const devicesSnapshot = await getDocs(devicesRef);
      const devicesMap = {};
      devicesSnapshot.docs.forEach(doc => {
        devicesMap[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Fetch all routes for trip information
      const routesRef = collection(db, 'optimizedRoutes');
      const routesSnapshot = await getDocs(routesRef);
      const routesMap = {};
      routesSnapshot.docs.forEach(doc => {
        const route = doc.data();
        if (route.busId) {
          if (!routesMap[route.busId]) {
            routesMap[route.busId] = [];
          }
          routesMap[route.busId].push({ id: doc.id, ...route });
        }
      });

      // Fetch driver profile pictures
      const driverIds = new Set();
      vehiclesSnapshot.docs.forEach(vehicleDoc => {
        const vehicle = vehicleDoc.data();
        if (vehicle.assignedDriver && vehicle.assignedDriver.id) {
          driverIds.add(vehicle.assignedDriver.id);
        }
      });

      const profilesMap = {};
      for (const driverId of driverIds) {
        try {
          const driverDoc = await getDoc(doc(db, 'users', driverId));
          if (driverDoc.exists()) {
            profilesMap[driverId] = driverDoc.data();
          }
        } catch (error) {
          console.error(`Error fetching driver profile for ${driverId}:`, error);
        }
      }
      setDriverProfiles(profilesMap);

      // Filter vehicles with ignition on
      const busesWithIgnitionList = [];
      filteredVehicles.forEach(vehicleDoc => {
        const vehicle = { id: vehicleDoc.id, ...vehicleDoc.data() };
        if (vehicle.assignedDevice && vehicle.assignedDevice.id) {
          const device = devicesMap[vehicle.assignedDevice.id];
          if (device && device.isIgnitionOn) {
            // Get active route for this bus
            const activeRoutes = routesMap[vehicle.id] || [];
            const currentRoute = activeRoutes.find(r => r.status === 'active') || activeRoutes[0] || null;
            
            busesWithIgnitionList.push({
              vehicle,
              device,
              route: currentRoute,
              location: device.location || 'Unknown',
              lat: parseFloat(device.lat) || 0,
              lon: parseFloat(device.lon) || 0
            });
          }
        }
      });

      setBusesWithIgnition(busesWithIgnitionList);
      if (busesWithIgnitionList.length > 0) {
        setCurrentBusIndex(0);
      }
    } catch (error) {
      console.error('Error fetching buses with ignition:', error);
    } finally {
      setLoadingBuses(false);
    }
  }, [userRole, currentUserId]);

  useEffect(() => {
    fetchBusesWithIgnition();
  }, [fetchBusesWithIgnition]);

  const currentBus = busesWithIgnition[currentBusIndex] || null;
  const mapCenter = currentBus && currentBus.lat && currentBus.lon 
    ? { lat: currentBus.lat, lng: currentBus.lon }
    : { lat: -1.2921, lng: 36.8219 }; // Default to Nairobi

  const handlePrevBus = () => {
    setCurrentBusIndex(prev => (prev > 0 ? prev - 1 : busesWithIgnition.length - 1));
  };

  const handleNextBus = () => {
    setCurrentBusIndex(prev => (prev < busesWithIgnition.length - 1 ? prev + 1 : 0));
  };

  // Format kilometers to K or M
  const formatKilometers = (kilometers) => {
    if (kilometers >= 1000000) {
      return `${(kilometers / 1000000).toFixed(1)}M`;
    } else if (kilometers >= 1000) {
      return `${(kilometers / 1000).toFixed(1)}K`;
    }
    return kilometers.toFixed(2);
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Helper function to format date key in local timezone (YYYY-MM-DD)
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    const dateKey = formatDateKey(clickedDate);
    
    if (selectedDate && formatDateKey(selectedDate) === dateKey) {
      setSelectedDate(null); // Deselect if clicking same date
      setCurrentTripIndex(0);
    } else {
      setSelectedDate(clickedDate);
      setCurrentTripIndex(0); // Reset trip index when selecting new date
    }
  };

  const handlePrevTrip = () => {
    const trips = getSelectedDateTrips();
    setCurrentTripIndex(prev => (prev > 0 ? prev - 1 : trips.length - 1));
  };

  const handleNextTrip = () => {
    const trips = getSelectedDateTrips();
    setCurrentTripIndex(prev => (prev < trips.length - 1 ? prev + 1 : 0));
  };

  const handlePrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const getTripsForDate = (day) => {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    const dateKey = formatDateKey(date);
    return tripsByDate[dateKey] || [];
  };

  const getSelectedDateTrips = () => {
    if (!selectedDate) return [];
    const dateKey = formatDateKey(selectedDate);
    return tripsByDate[dateKey] || [];
  };

  const getTripTypeColor = (tripType) => {
    if (tripType === 'AM') return '#3B82F6'; // Blue for School Trip
    if (tripType === 'PM') return '#92400E'; // Brown for Home Trip
    if (tripType === 'FIELD_TRIP') return '#065F46'; // Dark green for Field Trip
    return '#3B82F6'; // Default to blue
  };

  const handlePrevTweet = () => {
    setCurrentTweetIndex(prev => (prev > 0 ? prev - 1 : tweets.length - 1));
  };

  const handleNextTweet = () => {
    setCurrentTweetIndex(prev => (prev < tweets.length - 1 ? prev + 1 : 0));
  };

  const handleNextTweetMedia = () => {
    const currentTweet = tweets[currentTweetIndex];
    if (!currentTweet || !Array.isArray(currentTweet.mediaUrls) || currentTweet.mediaUrls.length <= 1) {
      return;
    }
    setCurrentTweetMediaIndex(prev => (prev + 1) % currentTweet.mediaUrls.length);
  };

  const handlePrevTweetMedia = () => {
    const currentTweet = tweets[currentTweetIndex];
    if (!currentTweet || !Array.isArray(currentTweet.mediaUrls) || currentTweet.mediaUrls.length <= 1) {
      return;
    }
    setCurrentTweetMediaIndex(prev => (prev - 1 + currentTweet.mediaUrls.length) % currentTweet.mediaUrls.length);
  };

  useEffect(() => {
    // Reset media index whenever tweet changes
    setCurrentTweetMediaIndex(0);
  }, [currentTweetIndex]);

  const formatTweetTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const handleCompareToggle = () => {
    setCompareMode(!compareMode);
  };

  const handleCustomDateRangeChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomDateApply = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      setChartDuration('custom');
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateCancel = () => {
    setShowCustomDatePicker(false);
    if (chartDuration === 'custom') {
      setChartDuration('1d');
    }
  };

  const userName = userInfo?.firstName || 'User';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your dashboard data</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardContent}>
        <div className={styles.greeting}>
          <div className={styles.greetingName}>Hey {userName}, {timeOfDay}!</div>
          <div className={styles.greetingSubtext}>here's what's up :)</div>
        </div>
      </div>
      <div className={styles.statsContainer}>
        <div className={styles.fleetHealthSummary}>
          <p className={styles.fleetHealthText}>
            Your fleet is operating smoothly with all systems running efficiently. 
            All active buses are on schedule and maintenance is up to date. 
            The transportation network is functioning at optimal capacity with drivers 
            ready and routes properly configured.
          </p>
          <div className={styles.fleetHealthTitle}>Andama Health Check</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaBus />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : activeBuses}</div>
            <div className={styles.statLabel}>Active Buses</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaWrench />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : busesInMaintenance}</div>
            <div className={styles.statLabel}>Buses in Maintenance</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaUser />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : driverCount}</div>
            <div className={styles.statLabel}>Drivers</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaUsers />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : studentCount}</div>
            <div className={styles.statLabel}>Students</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaRoute />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : scheduledTrips}</div>
            <div className={styles.statLabel}>Trips Scheduled (24h)</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FaTachometerAlt />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{loading ? '...' : formatKilometers(totalMiles)}</div>
            <div className={styles.statLabel}>KM planned (24h)</div>
          </div>
        </div>
      </div>
      
      {/* Metrics Section */}
      <div className={styles.metricsSection}>
        <div className={styles.metricsHeader}>
          <div className={styles.sharedDurationToggle}>
            {['1d', '1wk', '30days', 'future'].map(duration => (
              <button
                key={duration}
                className={`${styles.durationButton} ${chartDuration === duration ? styles.durationButtonActive : ''}`}
                onClick={() => {
                  setChartDuration(duration);
                  setShowCustomDatePicker(false);
                }}
              >
                {duration === '1d' ? '1D' : duration === '1wk' ? '1WK' : duration === '30days' ? '30D' : 'Future'}
              </button>
            ))}
            <div className={styles.customDateButtonWrapper}>
              <button
                className={`${styles.durationButton} ${chartDuration === 'custom' ? styles.durationButtonActive : ''} ${showCustomDatePicker ? styles.durationButtonActive : ''}`}
                onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
              >
                Custom
              </button>
              {showCustomDatePicker && (
                <div className={styles.customDatePicker}>
                  <div className={styles.customDatePickerContent}>
                    <div className={styles.customDatePickerRow}>
                      <label className={styles.customDatePickerLabel}>Start Date:</label>
                      <input
                        type="date"
                        value={customDateRange.startDate}
                        onChange={(e) => handleCustomDateRangeChange('startDate', e.target.value)}
                        className={styles.customDatePickerInput}
                      />
                    </div>
                    <div className={styles.customDatePickerRow}>
                      <label className={styles.customDatePickerLabel}>End Date:</label>
                      <input
                        type="date"
                        value={customDateRange.endDate}
                        onChange={(e) => handleCustomDateRangeChange('endDate', e.target.value)}
                        className={styles.customDatePickerInput}
                        min={customDateRange.startDate}
                      />
                    </div>
                    <div className={styles.customDatePickerActions}>
                      <button
                        onClick={handleCustomDateApply}
                        className={styles.customDatePickerButton}
                        disabled={!customDateRange.startDate || !customDateRange.endDate}
                      >
                        Apply
                      </button>
                      <button
                        onClick={handleCustomDateCancel}
                        className={styles.customDatePickerButtonCancel}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              className={`${styles.durationButton} ${compareMode ? styles.durationButtonActive : ''}`}
              onClick={handleCompareToggle}
            >
              Compare
            </button>
          </div>
        </div>
        <div className={styles.metricsGrid}>
          {/* Card 1: Total Distance of Fleet (long, top to bottom) */}
          <div className={`${styles.metricCardLong} ${styles.dashboardRadarCard}`}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>
                Fleet Event Cluster
                {chartDuration === '1d' && ' (1D)'}
                {chartDuration === '1wk' && ' (1WK)'}
                {chartDuration === '30days' && ' (30D)'}
                {chartDuration === 'future' && ' (Future)'}
                {chartDuration === 'custom' && ' (Custom)'}
              </h3>
            </div>
            <div className={`${styles.metricCardContent} ${styles.dashboardRadarCardContent}`}>
              <div className={styles.dashboardRadarContainer}>
                {loadingMetrics ? (
                  <div className={styles.chartLoading}>Loading event radar...</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={fleetEventRadarData} margin={{ top: 32, right: 48, left: 48, bottom: 28 }} outerRadius="58%">
                      <PolarGrid stroke="#9ca3af" strokeOpacity={0.8} />
                      <PolarAngleAxis dataKey="event" tick={<DashboardRadarAngleTick />} />
                      <PolarRadiusAxis allowDecimals={false} tick={{ fill: '#374151', fontSize: 10 }} />
                      <Tooltip />
                      <Radar
                        name="Event profile"
                        dataKey="value"
                        stroke="#6366f1"
                        fill="#818cf8"
                        fillOpacity={0.35}
                        strokeWidth={2.2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className={styles.dashboardRadarBadges}>
                {dashboardRadarEvents.map((eventItem) => (
                  <div key={`dashboard-badge-${eventItem.key}`} className={styles.dashboardRadarBadge}>
                    <span className={styles.dashboardRadarBadgeLabel}>
                      <span className={styles.dashboardRadarBadgeDot} style={{ backgroundColor: eventItem.color }} />
                      {eventItem.label}
                    </span>
                    <span className={styles.dashboardRadarBadgeValue}>
                      {loadingMetrics ? '...' : (fleetEventCounts[eventItem.key] || 0)}
                    </span>
                  </div>
                ))}
              </div>
              <div className={styles.dashboardRadarFootnote}>
                Total mileage reference: {loadingMetrics ? '...' : `${formatKilometers(totalFleetDistance)} KM`}
              </div>
            </div>
          </div>

          {/* Card 2: Actual Distance (top, right of total distance card) */}
          <div className={styles.metricCard}>
            <div className={styles.metricCardIcon}>
              <FaTachometerAlt />
            </div>
            <div className={styles.metricCardContent}>
              <div className={styles.metricCardNumber}>
                {loadingMetrics ? '...' : formatKilometers(actualDistance)}
              </div>
              <div className={styles.metricCardLabel}>Actual Distance Covered by Fleet (KM)</div>
            </div>
          </div>

          {/* Card 3: Planned Distance (below actual distance) */}
          <div className={styles.metricCard}>
            {!loadingMetrics && ((compareMode && previousPlannedDistance > 0) || (!compareMode && previousPlannedDistance > 0)) && (
              <div className={styles.comparisonMetric}>
                {(() => {
                  const percentageChange = previousPlannedDistance > 0 
                    ? ((plannedDistance - previousPlannedDistance) / previousPlannedDistance) * 100 
                    : 0;
                  const isIncrease = percentageChange > 0;
                  const absPercentage = Math.abs(percentageChange).toFixed(1);
                  
                  return (
                    <>
                      {isIncrease ? (
                        <FaArrowUp className={styles.comparisonArrowUp} />
                      ) : (
                        <FaArrowDown className={styles.comparisonArrowDown} />
                      )}
                      <span className={isIncrease ? styles.comparisonTextUp : styles.comparisonTextDown}>
                        {absPercentage}%
                      </span>
                    </>
                  );
                })()}
              </div>
            )}
            <div className={styles.metricCardIcon}>
              <FaRoute />
            </div>
            <div className={styles.metricCardContent}>
              <div className={styles.metricCardNumber}>
                {loadingMetrics ? '...' : formatKilometers(plannedDistance)}
              </div>
              <div className={styles.metricCardLabel}>
                Planned Distance of Fleet (KM)
                {chartDuration === '1d' && ' (1D)'}
                {chartDuration === '1wk' && ' (1WK)'}
                {chartDuration === '30days' && ' (30D)'}
                {chartDuration === 'future' && ' (Future)'}
                {chartDuration === 'custom' && ' (Custom)'}
              </div>
              {compareMode && !loadingMetrics && previousPlannedDistance > 0 && (
                <div className={styles.comparisonDetails}>
                  <div className={styles.comparisonPrevious}>
                    Previous: {formatKilometers(previousPlannedDistance)} KM
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chart 1: Distance Covered */}
          <div className={`${styles.metricChartCard} ${styles.distanceChartCard}`}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Distance (KM)</h3>
            </div>
            <div className={styles.chartContainer}>
              {loadingMetrics ? (
                <div className={styles.chartLoading}>Loading chart data...</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={distanceChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <XAxis 
                      dataKey="name" 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#e5e7eb' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#e5e7eb' }}
                      hide
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      labelStyle={{ color: '#1a1f2e', fontWeight: 600, marginBottom: '4px' }}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0] && payload[0].payload && payload[0].payload.fullDate) {
                          return payload[0].payload.fullDate.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });
                        }
                        return label;
                      }}
                      formatter={(value, name) => [value, 'Distance (KM)']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="distance" 
                      stroke="#14b8a6" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: '#14b8a6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Chart 2: Trips Covered */}
          <div className={`${styles.metricChartCard} ${styles.tripsChartCard}`}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Trips</h3>
            </div>
            <div className={styles.chartContainer}>
              {loadingMetrics ? (
                <div className={styles.chartLoading}>Loading chart data...</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={tripsChartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <XAxis 
                      dataKey="name" 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#e5e7eb' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#e5e7eb' }}
                      hide
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      labelStyle={{ color: '#1a1f2e', fontWeight: 600, marginBottom: '4px' }}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0] && payload[0].payload && payload[0].payload.fullDate) {
                          return payload[0].payload.fullDate.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });
                        }
                        return label;
                      }}
                      formatter={(value, name) => [value, 'Trips']}
                    />
                    <Bar 
                      dataKey="trips" 
                      fill="#14b8a6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Driver Status UI */}
          <div className={styles.driversStatusCard}>
            <div className={styles.driversStatusHeader}>
              <h3 className={styles.driversStatusTitle}>Drivers Status</h3>
            </div>
            <div className={styles.driverToggleContainer}>
              <div className={styles.driverToggle}>
                <button
                  className={`${styles.driverToggleButton} ${!showRestingDrivers ? styles.driverToggleButtonActive : ''}`}
                  onClick={() => setShowRestingDrivers(false)}
                >
                  On Trip ({driversOnTrip.length})
                </button>
                <button
                  className={`${styles.driverToggleButton} ${showRestingDrivers ? styles.driverToggleButtonActive : ''}`}
                  onClick={() => setShowRestingDrivers(true)}
                >
                  Resting ({driversResting.length})
                </button>
              </div>
            </div>
            <div className={styles.driversList}>
              {loadingMetrics ? (
                <div className={styles.driversLoading}>Loading drivers...</div>
              ) : (showRestingDrivers ? driversResting : driversOnTrip).length === 0 ? (
                <div className={styles.driversEmpty}>
                  No drivers {showRestingDrivers ? 'resting' : 'on trip'} at the moment
                </div>
              ) : (
                (showRestingDrivers ? driversResting : driversOnTrip).map((driver) => (
                  <div key={driver.id} className={styles.driverItem}>
                    {driver.profilePicture ? (
                      <img
                        src={driver.profilePicture}
                        alt={driver.firstName || 'Driver'}
                        className={styles.driverPhoto}
                      />
                    ) : (
                      <div className={styles.driverPhotoPlaceholder}>
                        {(driver.firstName?.[0] || 'D') + (driver.lastName?.[0] || '').toUpperCase()}
                      </div>
                    )}
                    <div className={styles.driverInfo}>
                      <div className={styles.driverName}>
                        {driver.firstName || ''} {driver.lastName || ''}
                      </div>
                      {driver.vehicle && (
                        <div className={styles.driverBus}>
                          <FaBus className={styles.driverBusIcon} />
                          {driver.vehicle.registrationNumber || driver.vehicle.fleetNumber || 'N/A'}
                        </div>
                      )}
                      {driver.route && (
                        <div className={styles.driverRoute}>
                          <FaRoute className={styles.driverRouteIcon} />
                          {driver.route.name || 'Active Trip'}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.leftStats}>
          <div className={styles.calendarContainer}>
            <div className={styles.calendarHeader}>
              <div className={styles.calendarTitle}>Trip Schedule</div>
              <div className={styles.calendarNavigation}>
                <button onClick={handlePrevMonth} className={styles.calendarNavButton}>
                  <FaChevronLeft />
                </button>
                <span className={styles.calendarMonthYear}>
                  {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={handleNextMonth} className={styles.calendarNavButton}>
                  <FaChevronRight />
                </button>
              </div>
            </div>
            <div className={styles.calendarGrid}>
              <div className={styles.calendarWeekdays}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className={styles.calendarWeekday}>{day}</div>
                ))}
              </div>
              <div className={styles.calendarDays}>
                {Array.from({ length: getFirstDayOfMonth(calendarMonth) }).map((_, idx) => (
                  <div key={`empty-${idx}`} className={styles.calendarDayEmpty}></div>
                ))}
                {Array.from({ length: getDaysInMonth(calendarMonth) }).map((_, idx) => {
                  const day = idx + 1;
                  const trips = getTripsForDate(day);
                  const tripCount = trips.length;
                  const isSelected = selectedDate && 
                    selectedDate.getDate() === day && 
                    selectedDate.getMonth() === calendarMonth.getMonth() &&
                    selectedDate.getFullYear() === calendarMonth.getFullYear();
                  const isToday = new Date().getDate() === day && 
                    new Date().getMonth() === calendarMonth.getMonth() &&
                    new Date().getFullYear() === calendarMonth.getFullYear();
                  
                  return (
                    <div
                      key={day}
                      className={`${styles.calendarDay} ${isSelected ? styles.calendarDaySelected : ''} ${isToday ? styles.calendarDayToday : ''}`}
                      onClick={() => handleDateClick(day)}
                    >
                      <div className={styles.calendarDayNumber}>{day}</div>
                      {tripCount > 0 && (
                        <div className={styles.calendarDayTripCount}>{tripCount}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {selectedDate && getSelectedDateTrips().length > 0 && (
              <div className={styles.calendarTripsList}>
                <div className={styles.calendarTripsHeader}>
                  <span>Trips on {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  {getSelectedDateTrips().length > 1 && (
                    <div className={styles.calendarTripsPagination}>
                      <button onClick={handlePrevTrip} className={styles.calendarTripsPaginationBtn}>
                        <FaChevronLeft />
                      </button>
                      <span className={styles.calendarTripsPaginationInfo}>
                        {currentTripIndex + 1} / {getSelectedDateTrips().length}
                      </span>
                      <button onClick={handleNextTrip} className={styles.calendarTripsPaginationBtn}>
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
                </div>
                <div className={styles.calendarTrips}>
                  {(() => {
                    const trips = getSelectedDateTrips();
                    const currentTrip = trips[currentTripIndex];
                    if (!currentTrip) return null;
                    return (
                      <div className={styles.calendarTripItem}>
                        <div className={styles.calendarTripColorIndicator} style={{ backgroundColor: getTripTypeColor(currentTrip.tripType) }}></div>
                        <div className={styles.calendarTripInfo}>
                          <div className={styles.calendarTripTime}>{currentTrip.time}</div>
                          <div className={styles.calendarTripName}>{currentTrip.name}</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.busTrackingContainer}>
          <div className={styles.busTrackingHeader}>
            <h3 className={styles.busTrackingTitle}>
              Buses with Ignition On ({busesWithIgnition.length})
            </h3>
            {busesWithIgnition.length > 1 && (
              <div className={styles.busPagination}>
                <button onClick={handlePrevBus} className={styles.busPaginationBtn}>
                  <FaChevronLeft />
                </button>
                <span className={styles.busPaginationInfo}>
                  {currentBusIndex + 1} / {busesWithIgnition.length}
                </span>
                <button onClick={handleNextBus} className={styles.busPaginationBtn}>
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
          {loadingBuses ? (
            <div className={styles.busTrackingLoading}>Loading buses...</div>
          ) : busesWithIgnition.length === 0 ? (
            <div className={styles.busTrackingEmpty}>No buses with ignition on</div>
          ) : currentBus && (
            <div className={styles.busTrackingContent}>
              <div className={styles.busMapContainer}>
                {isLoaded && (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '16px' }}
                    center={mapCenter}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                    }}
                  >
                    {currentBus.lat && currentBus.lon && isLoaded && window.google && window.google.maps && (
                      <Marker
                        position={{ lat: currentBus.lat, lng: currentBus.lon }}
                        icon={createBusIcon(32)}
                      />
                    )}
                  </GoogleMap>
                )}
              </div>
              <div className={styles.busDetails}>
                <div className={styles.busDetailItem}>
                  <h4 className={styles.busDetailLabel}>Vehicle</h4>
                  <p className={styles.busDetailValue}>
                    {currentBus.vehicle.registrationNumber || currentBus.vehicle.fleetNumber || 'N/A'}
                  </p>
                </div>
                <div className={styles.busDetailItem}>
                  <h4 className={styles.busDetailLabel}>Location</h4>
                  <p className={styles.busDetailValue}>
                    <FaMapMarkerAlt className={styles.busDetailIcon} />
                    {currentBus.location}
                  </p>
                </div>
                {currentBus.route && (
                  <div className={styles.busDetailItem}>
                    <h4 className={styles.busDetailLabel}>Trip Details</h4>
                    <p className={styles.busDetailValue}>
                      <FaRoute className={styles.busDetailIcon} />
                      {currentBus.route.name || 'No active trip'}
                    </p>
                    {currentBus.route.time && (
                      <p className={styles.busDetailSubValue}>
                        Time: {currentBus.route.time}
                      </p>
                    )}
                    {currentBus.route.rosterName && (
                      <p className={styles.busDetailSubValue}>
                        Roster: {currentBus.route.rosterName}
                      </p>
                    )}
                  </div>
                )}
                {currentBus.vehicle.assignedDriver && (
                  <div className={styles.busDetailItem}>
                    <h4 className={styles.busDetailLabel}>Driver</h4>
                    <div className={styles.busDriverInfo}>
                      {driverProfiles[currentBus.vehicle.assignedDriver.id]?.profilePicture ? (
                        <img
                          src={driverProfiles[currentBus.vehicle.assignedDriver.id].profilePicture}
                          alt={currentBus.vehicle.assignedDriver.name}
                          className={styles.busDriverPhoto}
                        />
                      ) : (
                        <div className={styles.busDriverPhotoPlaceholder}>
                          {currentBus.vehicle.assignedDriver.name 
                            ? currentBus.vehicle.assignedDriver.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'D'}
                        </div>
                      )}
                      <div className={styles.busDriverDetails}>
                        <p className={styles.busDetailValue}>
                          {currentBus.vehicle.assignedDriver.name || 'Not assigned'}
                        </p>
                        {currentBus.vehicle.assignedDriver.phone && (
                          <p className={styles.busDetailSubValue}>
                            Phone: {currentBus.vehicle.assignedDriver.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.alertsContainer}>
          <div className={styles.alertsHeader}>
            <h3 className={styles.alertsTitle}>Fleet Alerts</h3>
          </div>
          <div className={styles.alertsList}>
            {fleetAlerts.length === 0 ? (
              <div className={styles.noAlerts}>No alerts at this time</div>
            ) : (
              fleetAlerts.map((alert) => {
                const IconComponent = alert.icon;
                const getAlertIconColor = () => {
                  if (alert.type === 'warning') return '#fbbf24';
                  if (alert.type === 'error') return '#ef4444';
                  return '#00E0E0';
                };
                
                return (
                  <div key={alert.id} className={styles.alertItem}>
                    <div className={styles.alertIcon} style={{ color: getAlertIconColor() }}>
                      <IconComponent />
                    </div>
                    <div className={styles.alertContent}>
                      <div className={styles.alertTitle}>{alert.title}</div>
                      <div className={styles.alertMessage}>{alert.message}</div>
                      <div className={styles.alertTimestamp}>
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className={styles.newsWidget}>
          <div className={styles.newsHeader}>
            <div className={styles.newsTitle}>
              <FaTwitter className={styles.newsIcon} />
              <span>@{preferredTwitterUsername} News</span>
            </div>
            {tweets.length > 1 && (
              <div className={styles.newsPagination}>
                <button onClick={handlePrevTweet} className={styles.newsPaginationBtn}>
                  <FaChevronLeft />
                </button>
                <span className={styles.newsPaginationInfo}>
                  {currentTweetIndex + 1} / {tweets.length}
                </span>
                <button onClick={handleNextTweet} className={styles.newsPaginationBtn}>
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
          <div className={styles.newsContent}>
            {loadingTweets ? (
              <div className={styles.newsLoading}>Loading tweets...</div>
            ) : tweets.length === 0 ? (
              <div className={styles.newsEmpty}>No tweets available</div>
            ) : tweets[currentTweetIndex] && (
              <div className={styles.newsTweet}>
                <div className={styles.newsTweetText}>
                  {tweets[currentTweetIndex].text}
                </div>
                {Array.isArray(tweets[currentTweetIndex].mediaUrls) && tweets[currentTweetIndex].mediaUrls.length > 0 && (
                  <div className={styles.newsTweetMediaWrapper}>
                    <img
                      src={tweets[currentTweetIndex].mediaUrls[currentTweetMediaIndex]}
                      alt={`Tweet media ${currentTweetMediaIndex + 1}`}
                      className={styles.newsTweetMediaImage}
                      loading="lazy"
                    />
                    {tweets[currentTweetIndex].mediaUrls.length > 1 && (
                      <>
                        <button
                          type="button"
                          className={styles.newsTweetMediaPrevButton}
                          onClick={handlePrevTweetMedia}
                          aria-label="Previous tweet image"
                        >
                          <FaChevronLeft />
                        </button>
                        <button
                          type="button"
                          className={styles.newsTweetMediaNextButton}
                          onClick={handleNextTweetMedia}
                          aria-label="Next tweet image"
                        >
                          <FaChevronRight />
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className={styles.newsTweetMeta}>
                  <span className={styles.newsTweetAuthor}>@{tweets[currentTweetIndex].author}</span>
                  <span className={styles.newsTweetTime}>
                    {formatTweetTime(tweets[currentTweetIndex].createdAt)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 